import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  doc, getDoc, setDoc, serverTimestamp, collection, writeBatch, updateDoc, addDoc,
  query, where, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useFloorWorkers, useFloorWorkerRates, useCompanyUsers } from '../../hooks/useCompanyData';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useLabourCostCalculation } from '../../hooks/useLabourCostCalculation';
import { formatDateTime } from '../../utils/time';
import {
  Save, RefreshCw, Lock, Plus, Trash2, Pencil, X, Check, Info,
  ToggleLeft, ToggleRight, AlertCircle, History,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import './AdminLabourRatesPage.css';

const CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP', 'AUD'];

// ── Helpers ────────────────────────────────────────────────────────────────

function getCurrencySymbol(currency) {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency', currency,
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? '$';
  } catch {
    return '$';
  }
}

function WorkerAvatar({ name, inactive }) {
  const initial = name?.trim()?.[0]?.toUpperCase() ?? '?';
  return (
    <div className={`lr-worker-avatar${inactive ? ' lr-worker-avatar--inactive' : ''}`}>
      {initial}
    </div>
  );
}

function getAuditSubject(log) {
  if (log.action === 'set_default_team_rate') return 'Default team rate';
  return log.workerName || '—';
}

function describeAuditAction(log, sym) {
  if (log.action === 'add_worker_rate') {
    const r = log.after?.hourlyRate;
    return `Added at ${sym}${r != null ? Number(r).toFixed(2) : '?'}/hr`;
  }
  if (log.action === 'update_worker_rate') {
    const prev = log.before?.hourlyRate != null ? `${sym}${Number(log.before.hourlyRate).toFixed(2)}` : '—';
    const next = log.after?.hourlyRate != null ? `${sym}${Number(log.after.hourlyRate).toFixed(2)}` : '—';
    return `${prev} → ${next}/hr`;
  }
  if (log.action === 'set_default_team_rate') {
    const r = log.after?.defaultTeamHourlyRate;
    return `Set to ${sym}${r != null ? Number(r).toFixed(2) : '0.00'}/hr`;
  }
  return log.action ?? '—';
}

// ── Page ───────────────────────────────────────────────────────────────────

export default function AdminLabourRatesPage() {
  const { t } = useTranslation();
  const { workers, loading: workersLoading, error: workersError } = useFloorWorkers();
  const { rates, loading: ratesLoading, error: ratesError } = useFloorWorkerRates();
  const { users } = useCompanyUsers();
  const { user: currentUser, companyId } = useAuth();
  const toast = useToast();
  const { calculating, lastRun, runCalculation } = useLabourCostCalculation();

  // Labour settings
  const [defaultRate, setDefaultRate] = useState('');
  const [currency, setCurrency] = useState('CAD');
  const [savingDefault, setSavingDefault] = useState(false);
  const [adminSettingsLoaded, setAdminSettingsLoaded] = useState(false);

  // Add-worker panel
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', hourlyRate: '' });
  const [addSaving, setAddSaving] = useState(false);

  // Inline edit
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', hourlyRate: '' });
  const [editSaving, setEditSaving] = useState(false);

  // Rate change confirmation
  const [pendingRateConfirm, setPendingRateConfirm] = useState(null);

  // Delete confirmation
  const [confirmDeleteWorker, setConfirmDeleteWorker] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Active toggle
  const [togglingId, setTogglingId] = useState(null);

  // View options
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Audit log
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  const currencySymbol = getCurrencySymbol(currency);

  const usersMap = useMemo(
    () => Object.fromEntries(users.map((u) => [u.id, u.displayName || u.email || 'Admin'])),
    [users],
  );

  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, COLLECTIONS.ADMIN_SETTINGS, companyId)).then((snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setDefaultRate(d.defaultTeamHourlyRate ?? '');
        setCurrency(d.labourCostCurrency ?? 'CAD');
      }
      setAdminSettingsLoaded(true);
    }).catch(() => setAdminSettingsLoaded(true));
  }, [companyId]);

  // Live-subscribe to rate audit trail.
  // Requires composite index: activityLogs (companyId ASC, entityType ASC, createdAt DESC).
  useEffect(() => {
    if (!companyId) { setAuditLoading(false); return; }
    const q = query(
      collection(db, COLLECTIONS.ACTIVITY_LOGS),
      where('companyId', '==', companyId),
      where('entityType', '==', 'labourRate'),
      orderBy('createdAt', 'desc'),
      limit(30),
    );
    return onSnapshot(q, (snap) => {
      setAuditLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setAuditLoading(false);
    }, (err) => {
      console.error('[Floora] rate audit log:', err);
      setAuditLoading(false);
    });
  }, [companyId]);

  const logWageChange = (entry) => {
    addDoc(collection(db, COLLECTIONS.ACTIVITY_LOGS), {
      companyId,
      userId: currentUser.uid,
      entityType: 'labourRate',
      createdAt: serverTimestamp(),
      ...entry,
    }).catch((err) => console.error('[Floora] wage audit log failed:', err));
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSaveDefault = async () => {
    const rate = defaultRate !== '' ? parseFloat(defaultRate) : null;
    if (rate !== null && rate < 0) { toast(t('validation.negativeRate'), 'error'); return; }
    setSavingDefault(true);
    try {
      await setDoc(doc(db, COLLECTIONS.ADMIN_SETTINGS, companyId), {
        companyId,
        defaultTeamHourlyRate: rate,
        labourCostCurrency: currency,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
      }, { merge: true });
      logWageChange({ action: 'set_default_team_rate', after: { defaultTeamHourlyRate: rate, currency } });
      runCalculation({ force: true });
      toast(t('settings.saved'), 'success');
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSavingDefault(false);
    }
  };

  const handleAddWorker = async () => {
    const name = addForm.name.trim();
    const rate = parseFloat(addForm.hourlyRate);
    if (!name) { toast(t('toast.workerNameRequired'), 'error'); return; }
    if (isNaN(rate) || rate < 0) { toast(t('validation.negativeRate'), 'error'); return; }
    setAddSaving(true);
    try {
      const workerRef = doc(collection(db, COLLECTIONS.FLOOR_WORKERS));
      const batch = writeBatch(db);
      batch.set(workerRef, {
        companyId, name, active: true,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: currentUser.uid,
      });
      batch.set(doc(db, COLLECTIONS.FLOOR_WORKER_RATES, workerRef.id), {
        companyId, hourlyRate: rate, currency,
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: currentUser.uid,
      });
      await batch.commit();
      logWageChange({ action: 'add_worker_rate', entityId: workerRef.id, workerName: name, after: { hourlyRate: rate, currency } });
      setAddForm({ name: '', hourlyRate: '' });
      setShowAddForm(false);
      runCalculation({ force: true });
      toast(t('toast.workerAdded'), 'success');
    } catch (err) {
      console.error('[Floora] handleAddWorker failed:', err);
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setAddSaving(false);
    }
  };

  const startEdit = (worker) => {
    setEditingId(worker.id);
    setEditForm({ name: worker.name, hourlyRate: rates[worker.id]?.hourlyRate ?? '' });
  };

  const handleSaveEdit = async () => {
    const name = editForm.name.trim();
    const rate = parseFloat(editForm.hourlyRate);
    if (!name) { toast(t('toast.workerNameRequired'), 'error'); return; }
    if (isNaN(rate) || rate < 0) { toast(t('validation.negativeRate'), 'error'); return; }
    setEditSaving(true);
    const prevRate = rates[editingId]?.hourlyRate ?? null;
    const prevName = workers.find((w) => w.id === editingId)?.name ?? null;
    const editedWorkerId = editingId;
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, COLLECTIONS.FLOOR_WORKERS, editingId), {
        name, updatedAt: serverTimestamp(), updatedBy: currentUser.uid,
      });
      batch.set(doc(db, COLLECTIONS.FLOOR_WORKER_RATES, editingId), {
        companyId, hourlyRate: rate, currency,
        updatedAt: serverTimestamp(), updatedBy: currentUser.uid,
      }, { merge: true });
      await batch.commit();
      if (prevRate !== rate) {
        logWageChange({
          action: 'update_worker_rate', entityId: editedWorkerId, workerName: name,
          before: { hourlyRate: prevRate, name: prevName },
          after: { hourlyRate: rate, currency },
        });
      }
      setEditingId(null);
      runCalculation({ force: true });
      toast(t('toast.workerUpdated'), 'success');
    } catch (err) {
      console.error('[Floora] handleSaveEdit failed:', err);
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setEditSaving(false);
    }
  };

  // Show a rate-change confirmation only when the numeric rate value actually
  // differs. Name-only edits skip the dialog and save immediately.
  const requestSaveEdit = () => {
    const name = editForm.name.trim();
    const rate = parseFloat(editForm.hourlyRate);
    if (!name) { toast(t('toast.workerNameRequired'), 'error'); return; }
    if (isNaN(rate) || rate < 0) { toast(t('validation.negativeRate'), 'error'); return; }
    const prevRate = rates[editingId]?.hourlyRate ?? null;
    if (prevRate !== null && rate !== prevRate) {
      const workerName = workers.find((w) => w.id === editingId)?.name ?? name;
      setPendingRateConfirm({ workerName, prevRate, newRate: rate });
      return;
    }
    handleSaveEdit();
  };

  const handleToggleActive = async (worker) => {
    setTogglingId(worker.id);
    try {
      await updateDoc(doc(db, COLLECTIONS.FLOOR_WORKERS, worker.id), {
        active: !worker.active, updatedAt: serverTimestamp(), updatedBy: currentUser.uid,
      });
      toast(worker.active ? t('toast.workerDeactivated') : t('toast.workerActivated'), 'success');
    } catch (err) {
      console.error('[Floora] handleToggleActive failed:', err);
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDeleteWorker) return;
    const { id } = confirmDeleteWorker;
    setDeletingId(id);
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, COLLECTIONS.FLOOR_WORKERS, id));
      batch.delete(doc(db, COLLECTIONS.FLOOR_WORKER_RATES, id));
      await batch.commit();
      setConfirmDeleteWorker(null);
      toast(t('toast.workerRemoved'), 'success');
    } catch (err) {
      console.error('[Floora] handleDelete failed:', err);
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const activeWorkers = workers.filter((w) => w.active !== false);
  const inactiveWorkers = workers.filter((w) => w.active === false);
  const visibleWorkers = showInactive ? workers : activeWorkers;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>

      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <h1 className="lr-page-title">
            {t('admin.labourRates')}
            <button
              className="btn btn-ghost btn-icon lr-info-btn"
              onClick={() => setShowInfoModal(true)}
              title={t('labourRates.howCalculatedTitle')}
            >
              <Info size={16} />
            </button>
          </h1>
          <p className="page-header-sub lr-admin-note">
            <Lock size={12} />
            {t('labourRates.adminOnlyHint')}
          </p>
        </div>
        <button
          className="btn btn-secondary lr-recalc-btn"
          onClick={() => runCalculation({ force: true })}
          disabled={calculating}
          title={t('labourRates.recalcInfoTitle')}
        >
          <RefreshCw size={15} className={calculating ? 'spin-icon' : ''} />
          <span className="lr-recalc-label">
            {calculating ? t('admin.calculating') : t('admin.recalcMissingCosts')}
          </span>
        </button>
      </div>

      <div className="page-content-wide">

        {lastRun && (
          <div className="alert alert-info lr-last-calc">
            {t('admin.labourCostsLastCalc')}: {formatDateTime(lastRun)}
          </div>
        )}

        {/* ── Labour Settings ── */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">{t('labourRates.pageTitle')}</span>
          </div>
          <div className="card-body">
            <div className="lr-settings-row">
              <div className="form-group lr-settings-rate">
                <label className="form-label">{t('labourRates.defaultHourlyRate')}</label>
                <div className="rate-input-wrap">
                  <span className="rate-prefix">{currencySymbol}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    placeholder="0.00"
                    value={defaultRate}
                    onChange={(e) => setDefaultRate(e.target.value)}
                    disabled={!adminSettingsLoaded}
                  />
                </div>
              </div>
              <div className="form-group lr-settings-currency">
                <label className="form-label">{t('settings.labourCurrency')}</label>
                <select
                  className="form-control"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="lr-settings-action">
                <button
                  className="btn btn-primary"
                  onClick={handleSaveDefault}
                  disabled={savingDefault || !adminSettingsLoaded}
                >
                  {savingDefault ? <span className="spinner" /> : <><Save size={15} />{t('common.save')}</>}
                </button>
              </div>
            </div>
            <p className="form-hint lr-settings-hint">
              {t('labourRates.defaultRateHint')}
            </p>
          </div>
        </div>

        {/* ── Floor Workers ── */}
        <div className="card">
          <div className="card-header">
            <div className="lr-workers-header-left">
              <span className="card-title">{t('labourRates.floorWorkers')}</span>
              {!workersLoading && (
                <span className="lr-count-chip">
                  {t('labourRates.activeCount', { count: activeWorkers.length })}
                </span>
              )}
            </div>
            <div className="lr-workers-header-right">
              {!workersLoading && inactiveWorkers.length > 0 && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowInactive((v) => !v)}
                >
                  {showInactive
                    ? t('labourRates.hideInactive', { count: inactiveWorkers.length })
                    : t('labourRates.showInactive', { count: inactiveWorkers.length })}
                </button>
              )}
              <button
                className={`btn btn-sm ${showAddForm ? 'btn-secondary' : 'btn-primary'}`}
                onClick={() => {
                  setShowAddForm((v) => !v);
                  if (!showAddForm) setAddForm({ name: '', hourlyRate: '' });
                }}
              >
                {showAddForm ? <><X size={14} />{t('common.cancel')}</> : <><Plus size={14} />{t('labourRates.addWorker')}</>}
              </button>
            </div>
          </div>

          {/* Add-worker panel */}
          {showAddForm && (
            <div className="lr-add-panel">
              <div className="form-group lr-add-name">
                <label className="form-label">{t('labourRates.name')}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('labourRates.workerNamePlaceholder')}
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddWorker(); }}
                  autoFocus
                />
              </div>
              <div className="form-group lr-add-rate">
                <label className="form-label">{t('labourRates.hourlyRate')}</label>
                <div className="rate-input-wrap">
                  <span className="rate-prefix">{currencySymbol}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    placeholder="0.00"
                    value={addForm.hourlyRate}
                    onChange={(e) => setAddForm((f) => ({ ...f, hourlyRate: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddWorker(); }}
                  />
                </div>
              </div>
              <div className="lr-add-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleAddWorker}
                  disabled={addSaving}
                >
                  {addSaving ? <span className="spinner" /> : <><Plus size={13} />{t('labourRates.addWorker')}</>}
                </button>
              </div>
            </div>
          )}

          {/* Error state */}
          {(workersError || ratesError) ? (
            <div className="alert alert-error lr-load-error">
              <AlertCircle size={15} />
              <span>
                {t('labourRates.loadError')}{' '}
                <button className="btn-link" onClick={() => window.location.reload()}>
                  {t('labourRates.loadErrorHint')}
                </button>
              </span>
            </div>
          ) : workersLoading ? (
            <div className="page-loading" style={{ minHeight: 120 }}>
              <div className="spinner" />
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table lr-workers-table">
                <thead>
                  <tr>
                    <th>{t('labourRates.worker')}</th>
                    <th style={{ width: '22%' }}>{t('labourRates.rate')} ({currency}/hr)</th>
                    <th className="lr-col-updated" style={{ width: '20%' }}>{t('labourRates.lastUpdated')}</th>
                    <th style={{ width: '130px' }}>{t('taskTable.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleWorkers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="lr-empty-state">
                        <div className="lr-empty-inner">
                          <p className="lr-empty-title">{t('labourRates.emptyWorkers')}</p>
                          <p className="lr-empty-sub">{t('labourRates.emptyWorkersHint')}</p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {visibleWorkers.map((w) => (
                    editingId === w.id ? (
                      <tr key={w.id} className="lr-row-editing">
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                            style={{ maxWidth: 240 }}
                            autoFocus
                          />
                        </td>
                        <td>
                          <div className="rate-input-wrap" style={{ maxWidth: 140 }}>
                            <span className="rate-prefix">{currencySymbol}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control"
                              placeholder="0.00"
                              value={editForm.hourlyRate}
                              onChange={(e) => setEditForm((f) => ({ ...f, hourlyRate: e.target.value }))}
                            />
                          </div>
                        </td>
                        <td className="lr-col-updated" />
                        <td>
                          <div className="lr-actions">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={requestSaveEdit}
                              disabled={editSaving}
                            >
                              {editSaving ? <span className="spinner" /> : <><Check size={13} />{t('common.save')}</>}
                            </button>
                            <button
                              className="btn btn-sm btn-ghost lr-icon-btn"
                              onClick={() => setEditingId(null)}
                              title={t('common.cancel')}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={w.id} className={w.active === false ? 'lr-row-inactive' : ''}>
                        <td>
                          <div className="lr-worker-cell">
                            <WorkerAvatar name={w.name} inactive={w.active === false} />
                            <div className="lr-worker-cell-info">
                              <span className="lr-worker-name">{w.name}</span>
                              {w.active === false && (
                                <span className="lr-inactive-tag">{t('labourRates.inactive')}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          {ratesLoading ? (
                            <span className="text-muted">—</span>
                          ) : rates[w.id]?.hourlyRate != null ? (
                            <span className="lr-rate-value">
                              {currencySymbol}{Number(rates[w.id].hourlyRate).toFixed(2)}<span className="lr-rate-unit">/hr</span>
                            </span>
                          ) : (
                            <span className="lr-rate-missing">{t('labourRates.notSet')}</span>
                          )}
                        </td>
                        <td className="lr-col-updated lr-updated-cell">
                          {w.updatedAt
                            ? formatDateTime(w.updatedAt?.toDate?.() ?? w.updatedAt)
                            : '—'}
                        </td>
                        <td>
                          <div className="lr-actions">
                            <button
                              className="btn btn-sm btn-ghost lr-icon-btn"
                              onClick={() => startEdit(w)}
                              title={t('labourRates.editWorkerTitle')}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-ghost lr-icon-btn"
                              onClick={() => handleToggleActive(w)}
                              disabled={togglingId === w.id}
                              title={w.active === false ? t('labourRates.reactivateWorkerTitle') : t('labourRates.deactivateWorkerTitle')}
                            >
                              {togglingId === w.id
                                ? <span className="spinner" />
                                : w.active === false
                                  ? <ToggleRight size={15} />
                                  : <ToggleLeft size={15} />}
                            </button>
                            <button
                              className="btn btn-sm btn-ghost lr-icon-btn lr-icon-btn--danger"
                              onClick={() => setConfirmDeleteWorker({ id: w.id, name: w.name })}
                              disabled={deletingId === w.id}
                              title={t('labourRates.removeWorkerTitle')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Rate Change History ── */}
        <div className="card lr-history-card">
          <div className="card-header">
            <span className="card-title lr-history-title">
              <History size={15} />
              Rate Change History
            </span>
            <span className="lr-history-meta">Last 30 changes · audit log</span>
          </div>

          {auditLoading ? (
            <div className="page-loading" style={{ minHeight: 72 }}>
              <div className="spinner" />
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="lr-log-empty">{t('labourRates.noAuditLogs')}</div>
          ) : (
            <div className="lr-log-list">
              {auditLogs.map((log) => {
                const date = log.createdAt?.toDate?.() ?? log.createdAt;
                return (
                  <div key={log.id} className="lr-log-entry">
                    <div className="lr-log-dot" />
                    <div className="lr-log-content">
                      <span className="lr-log-subject">{getAuditSubject(log)}</span>
                      <span className="lr-log-sep">·</span>
                      <span className="lr-log-change">{describeAuditAction(log, currencySymbol)}</span>
                    </div>
                    <div className="lr-log-right">
                      <span className="lr-log-by">{usersMap[log.userId] ?? '—'}</span>
                      <span className="lr-log-date">{date ? formatDateTime(date) : '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* ── Dialogs ── */}

      {confirmDeleteWorker && (
        <ConfirmDialog
          title={t('labourRates.removeWorkerTitle_dialog')}
          message={t('labourRates.removeWorkerMessage', { name: confirmDeleteWorker.name })}
          confirmLabel={t('labourRates.confirmRemove')}
          confirmClass="btn-danger"
          loading={deletingId === confirmDeleteWorker.id}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDeleteWorker(null)}
        />
      )}

      {pendingRateConfirm && (
        <ConfirmDialog
          title={t('labourRates.updateRateTitle')}
          message={t('labourRates.updateRateMessage', {
            name: pendingRateConfirm.workerName,
            currency: currencySymbol,
            prevRate: Number(pendingRateConfirm.prevRate).toFixed(2),
            newRate: Number(pendingRateConfirm.newRate).toFixed(2),
          })}
          confirmLabel={t('labourRates.confirmUpdateRate')}
          confirmClass="btn-primary"
          loading={editSaving}
          onConfirm={() => { setPendingRateConfirm(null); handleSaveEdit(); }}
          onCancel={() => setPendingRateConfirm(null)}
        />
      )}

      {showInfoModal && (
        <Modal title={t('labourRates.howCalculatedTitle')} onClose={() => setShowInfoModal(false)}>
          <p className="text-sm" style={{ lineHeight: 1.7 }}>{t('labourRates.howCalculatedBody')}</p>
        </Modal>
      )}

    </div>
  );
}
